/**
 * Objectives & Governance Section
 * Fetched from CMS API
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';

const CMS_API_URL = process.env.REACT_APP_API_BASE_URL_CMS || '';
const OBJECTIVES_UID = 'OBJECTIVES-SECTION';
const GOVERNANCE_UID = 'GOVERNANCE-SECTION';
const BOARD_UID = 'BOARD-SECTION';

// Helper to parse HTML list items from Description
const parseObjectivesFromHtml = (html) => {
  if (!html) return [];
  // Extract text between <li> tags
  const liMatches = html.match(/<li[^>]*>(.*?)<\/li>/gi);
  if (liMatches) {
    return liMatches
      .map(li => li.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim())
      .filter(text => text.length > 0);
  }
  // Fallback: split by periods if no li tags
  return html
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/g, ' ')
    .split(/\.(?=[A-Z])|\.\s+/)
    .map(s => s.trim())
    .filter(s => s.length > 10);
};

// Helper to parse responsibilities from HTML
const parseResponsibilitiesFromHtml = (html) => {
  if (!html) return [];
  const liMatches = html.match(/<li[^>]*>(.*?)<\/li>/gi);
  if (liMatches) {
    return liMatches
      .map(li => li.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim())
      .filter(text => text.length > 0);
  }
  return html
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/g, ' ')
    .split(/\.(?=[A-Z])|\.\s+/)
    .map(s => s.trim())
    .filter(s => s.length > 10);
};

const SectionTitle = ({ children }) => (
  <div className="mb-5">
    <h3 className="text-xl md:text-2xl font-bold text-[#2f3951]">{children}</h3>
    <div className="w-16 h-1 bg-[#ca9a37] mt-2"></div>
  </div>
);

const ObjectivesGovernance = () => {
  const [objectives, setObjectives] = useState([]);
  const [objectivesTitle, setObjectivesTitle] = useState('');
  const [governanceText, setGovernanceText] = useState('');
  const [governanceTitle, setGovernanceTitle] = useState('');
  const [responsibilities, setResponsibilities] = useState([]);
  const [boardTitle, setBoardTitle] = useState('');
  const [loading, setLoading] = useState(true);

  // Fetch objectives, governance, and responsibilities from CMS API
  useEffect(() => {
    const fetchContent = async () => {
      try {
        // Fetch Objectives (UID: OBJECTIVES-SECTION)
        const objectivesResponse = await axios.post(
          `${CMS_API_URL}sections/get-by-uid`,
          { UID: OBJECTIVES_UID },
          {
            headers: { 'Content-Type': 'application/json' },
            timeout: 30000,
          }
        );

        if (objectivesResponse.data?.code === 1 && Array.isArray(objectivesResponse.data?.data) && objectivesResponse.data.data.length > 0) {
          const data = objectivesResponse.data.data[0];
          if (data.IsDeleted === false && data.IsPublished === true) {
            const parsed = parseObjectivesFromHtml(data.Description);
            if (parsed.length > 0) {
              setObjectives(parsed);
            }
            if (data.Title) {
              setObjectivesTitle(data.Title);
            }
          }
        }

        // Fetch Governance (UID: GOVERNANCE-SECTION)
        const governanceResponse = await axios.post(
          `${CMS_API_URL}sections/get-by-uid`,
          { UID: GOVERNANCE_UID },
          {
            headers: { 'Content-Type': 'application/json' },
            timeout: 30000,
          }
        );

        if (governanceResponse.data?.code === 1 && Array.isArray(governanceResponse.data?.data) && governanceResponse.data.data.length > 0) {
          const data = governanceResponse.data.data[0];
          if (data.IsDeleted === false && data.IsPublished === true) {
            const text = (data.Description || '')
              .replace(/<[^>]*>/g, '')
              .replace(/&nbsp;/g, ' ');
            setGovernanceText(text);
            if (data.Title) {
              setGovernanceTitle(data.Title);
            }
          }
        }

        // Fetch Board Responsibilities (UID: BOARD-SECTION)
        const boardResponse = await axios.post(
          `${CMS_API_URL}sections/get-by-uid`,
          { UID: BOARD_UID },
          {
            headers: { 'Content-Type': 'application/json' },
            timeout: 30000,
          }
        );

        if (boardResponse.data?.code === 1 && Array.isArray(boardResponse.data?.data) && boardResponse.data.data.length > 0) {
          const data = boardResponse.data.data[0];
          if (data.IsDeleted === false && data.IsPublished === true) {
            const parsed = parseResponsibilitiesFromHtml(data.Description);
            if (parsed.length > 0) {
              setResponsibilities(parsed);
            }
            if (data.Title) {
              setBoardTitle(data.Title);
            }
          }
        }
      } catch (error) {
        console.error('Failed to fetch objectives/governance content:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchContent();
  }, []);

  // Show loading skeleton while fetching data
  if (loading) {
    return (
      <section className="py-12 md:py-5 bg-white">
        <div className="container mx-auto px-6 md:px-8 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-6 lg:gap-8">
            <div className="bg-white rounded-lg p-6 md:p-8 border border-gray-100">
              <div className="h-8 w-40 bg-gray-200 rounded mb-5 animate-pulse" />
              <div className="w-16 h-1 bg-gray-200 mb-5 animate-pulse" />
              <ul className="space-y-4">
                {[1, 2, 3, 4].map((i) => (
                  <li key={i} className="h-4 w-full bg-gray-200 rounded animate-pulse" />
                ))}
              </ul>
            </div>
            <div className="space-y-6">
              <div className="bg-[#faf6f0] rounded-lg p-6 md:p-8">
                <div className="h-8 w-32 bg-gray-200 rounded mb-5 animate-pulse" />
                <div className="h-20 w-full bg-gray-200 rounded animate-pulse" />
              </div>
              <div className="bg-[#faf6f0] rounded-lg p-6 md:p-8">
                <div className="h-8 w-40 bg-gray-200 rounded mb-5 animate-pulse" />
                <ul className="space-y-3">
                  {[1, 2, 3, 4].map((i) => (
                    <li key={i} className="h-3 w-full bg-gray-200 rounded animate-pulse" />
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  // Return null if no data from API after loading
  if (objectives.length === 0 && !governanceText && responsibilities.length === 0 && !objectivesTitle && !governanceTitle && !boardTitle) {
    return null;
  }

  return (
    <section className="py-12 md:py-5 bg-white">
      <div className="container mx-auto px-6 md:px-8 lg:px-12">
        <div className="grid lg:grid-cols-2 gap-6 lg:gap-8">
          {/* Our Objectives */}
          <div className="bg-white rounded-lg p-6 md:p-8 border border-gray-100">
            {objectivesTitle && <SectionTitle>{objectivesTitle}</SectionTitle>}
            <ul className="space-y-4">
              {objectives.map((item, index) => (
                <li key={index} className="flex items-start">
                  <div className="w-5 h-5 bg-[#ca9a37] rounded-full flex items-center justify-center mt-0.5 mr-3 flex-shrink-0">
                    <div className="w-1.5 h-1.5 bg-white rounded-full"></div>
                  </div>
                  <span className="text-gray-600 text-sm leading-relaxed text-justify">{item}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Governance & Board Responsibilities */}
          <div className="space-y-6">
            {/* Governance */}
            <div className="bg-[#faf6f0] rounded-lg p-6 md:p-8">
              {governanceTitle && <SectionTitle>{governanceTitle}</SectionTitle>}
              <p className="text-gray-600 text-sm leading-relaxed text-justify">
                {governanceText}
              </p>
            </div>

            {/* Board Responsibilities */}
            <div className="bg-[#faf6f0] rounded-lg p-6 md:p-8">
              {boardTitle && <SectionTitle>{boardTitle}</SectionTitle>}
              <ul className="space-y-3">
                {responsibilities.map((item, index) => (
                  <li key={index} className="flex items-start">
                    <span className="text-[#ca9a37] mr-2 flex-shrink-0 text-xs mt-1">&#9656;</span>
                    <span className="text-gray-600 text-sm leading-relaxed text-justify">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ObjectivesGovernance;
